/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-key */
import PropTypes from 'prop-types';
import { useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
// use @mui
import { useTheme } from '@mui/material/styles';
import { Box, Stack, AppBar, Toolbar, IconButton, TextField, Autocomplete, Tooltip, Button, Menu, MenuItem, FormControl, Select, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
// use hooks
import { clearTablePreferences, getGlobalTablePreferences, resetTablePreferences } from 'src/redux/slices/tablePreferences';
import { resetWhatsAppNotifCount } from 'src/redux/slices/whatsapp';
import useFeaturePermission from 'src/hooks/useFeaturePermission';
import useGetCurrentUser from 'src/hooks/useGetCurrentUser';
import { useOffSetTop, useResponsive, useAuth } from '../../../hooks';
import Iconify from '../../../components/Iconify';
import useDemoMode from '../../../hooks/useDemoMode';
// utils
import { bgBlur } from '../../../utils/cssStyles';
import { localStorageRemoveItem, localStorageGetItem } from '../../../utils/storage-available';
import {
    resolvePracticeSwitchTarget,
    getRouteRequirement,
    resolveRequirementFallback,
    shouldPreserveQuery,
    shouldApplyPendingPracticeChange,
} from '../../../utils/practiceSwitchRoute';
// use config
import { HEADER, NAV } from '../../../config';
// components
import { SvgColor } from '../../../components/svg-color';
import { useSettingsContext } from '../../../components/settings/context';
//
import Searchbar from './Searchbar';
import AccountPopover from './AccountPopover';
import SettingsPopover from './SettingsPopover';
import SuperAdminPopover from './SuperAdminPopover';
import NotificationsPopover from './NotificationsPopover';
import PinnedLeadsPopover from './PinnedLeadsPopover';
import InstantVideoSwitch from '../../../components/instantVideo/InstantVideoSwitch';
import MyDeskChip from '../../../components/outbound/MyDeskChip';
import { HEADER_DESK_SLOT_ID } from '../../../components/outbound/headerDeskSlot';
// redux
import {
    getPracticeTreatmentListData,
    switchPractice,
    bumpPracticeSwitchEpoch,
    setPendingSwitchLanding,
    clearPendingSwitchLanding,
} from '../../../redux/slices/practiceTreatmentList';
import { setReminderData, setToDoData } from '../../../redux/slices/toDoNotifications';
import { addTreatmentsList, clearTreatmentsListForWidget, getChoicesList } from '../../../redux/slices/practiceTreatment';
import { dispatch, useSelector } from '../../../redux/store';
import { practiceSwitchReset } from '../../../redux/practiceSwitchReset';
import {
    updateAttendTable,
    updateNewTable,
    updateBookedTable,
    updateArchiveTable,
    updateDashboardTable,
    updateTreatmentStartedTable,
    updateDashNewLeadsTable,
    clearTableData,
    updateCounter,
    updateFailedToAttendTable,
    updateClosedTable,
    clearTaskLead,
    clearSeparateTaskCounter,
    reset as leadReset,
    getMergeTags,
    updateNoTaskTable,
} from '../../../redux/slices/lead';
import { getPracticeData, updateDentallyEnabledGlobal } from '../../../redux/slices/practice';
import usePmsSchedulingScope from '../../../hooks/usePmsSchedulingScope';
import useDentallyIntegrationScope from '../../../hooks/useDentallyIntegrationScope';
import { prefetchDentallyPractitioners, resetDentallyPractitioners } from '../../../redux/slices/dentallyPractitioners';
import { notificationData } from '../../../redux/slices/user';
import { clearData } from '../../../redux/slices/practiceUser';
import { audienceManagerReset } from '../../../redux/slices/audienceManager';
import { clearTreatmentCategoryReport, reset } from '../../../redux/slices/analytics';
import { reset as practiceIntegrationReset, getDentallyConfigs } from '../../../redux/slices/practiceIntegration';
import { reset as chatReset } from '../../../redux/slices/chat';
import { reset as callScriptReset } from '../../../redux/slices/callScript';
import { clearMediaImages } from '../../../redux/slices/campaign';
import { getSchemaList } from '../../../redux/slices/schema';
import { setEmailNotifications, setWpNotifications } from '../../../redux/slices/liveNotificationSlice';
import { setWpTemplateListGlobal } from '../../../redux/slices/whatsAppMessages';
import { fetchMyPermissions } from '../../../redux/slices/permissions';

// V4 mock-status selector constants (only shown on /lead-detail-v3/ and /lead-detail-v4/ routes)
const V3_MOCK_KEYS = [
    'MULTI_TREATMENT',
    'NEW',
    'CONSULTATION_BOOKED',
    'CONSULTATION_ATTENDED',
    'TREATMENT_STARTED',
    'CLOSED',
    'ARCHIVED',
];

const V3_MOCK_LABELS = {
    MULTI_TREATMENT: 'Multi-treatment lead',
    NEW: 'New lead',
    CONSULTATION_BOOKED: 'Consultation booked',
    CONSULTATION_ATTENDED: 'Consultation attended',
    TREATMENT_STARTED: 'Treatment started',
    CLOSED: 'Closed',
    ARCHIVED: 'Archived',
};

// ----------------------------------------------------------------------
export default function DashboardHeader({ onOpenSidebar, onOpenCommandPalette }) {
    const theme = useTheme();

    const { themeLayout } = useSettingsContext();

    const isNavHorizontal = themeLayout === 'horizontal';

    const isNavMini = themeLayout === 'mini';

    const isOffset = useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !isNavHorizontal;

    const navigate = useNavigate();
    const { pathname, search } = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const routeParams = useParams();
    const isDesktop = useResponsive('up', 'lg');

    const { practiceId, practiceTreatment, practiceIdToChange } = useSelector((state) => state.practiceTreatmentList);

    usePmsSchedulingScope(practiceId);

    const [selectedId, setSelectedId] = useState(Number(practiceId));

    const [demoMode, setDemoMode] = useDemoMode();
    const [demoMenuAnchor, setDemoMenuAnchor] = useState(null);
    const toggleDemoMode = () => setDemoMode((prev) => !prev);

    const { globalTablePreferences } = useSelector((state) => state.tablePreferences);
    const choicesData = useSelector((state) => state.practiceTreatment.choicesData.results);

    const practitionerDashboard = useFeaturePermission('practitioner-dashboard');
    const isPractitionerFlagUser = practitionerDashboard?.canView && !practitionerDashboard?.canManage;
    const dentallyFeature = useFeaturePermission('dentally-integration');
    // Signals that a permissions fetch has RESOLVED (200 or error) for a given
    // practice. loadingPracticeId is stamped when the fetch starts and retained
    // when it finishes, so `!loading && loadingPracticeId === practiceId` means
    // "we have a settled permissions answer for THIS practice" — the safe point
    // to trust dentallyFeature.canView === false and clear a stale global flag.
    const permLoading = useSelector((state) => state.permissions.loading);
    const permLoadingPracticeId = useSelector((state) => state.permissions.loadingPracticeId);
    // Success-stamped snapshot (set only by a 200 from fetchMyPermissions) plus
    // the map itself — the post-switch reconciliation below needs BOTH to know
    // it is reading the NEW practice's answers and not the outgoing one's.
    const permissionsMap = useSelector((state) => state.permissions.permissions);
    const permissionsPracticeId = useSelector((state) => state.permissions.practiceId);
    // { practiceId, pathname } captured when a switch leaves the operator on a
    // route whose availability is practice-dependent. Cleared as soon as it is
    // acted on, superseded, or the operator navigates elsewhere. Held in Redux
    // so <FeatureGuard> can agree on the fallback destination.
    const pendingSwitchLanding = useSelector((state) => state.practiceTreatmentList.pendingSwitchLanding);
    const { isPractitioner } = useGetCurrentUser();

    const { logout } = useAuth();

    useEffect(() => {
        const data = {
            limit: 10,
            offset: 0
        };
        dispatch(getPracticeTreatmentListData(handleCallBack, data));
    }, []);

    useEffect(() => {
        setSelectedId(practiceId);
        const getReferralData = localStorageGetItem('ReferralData') || [];
        const getMergeTagsData = localStorageGetItem('MergeTagsData') || [];
        const getSourceTypeData = localStorageGetItem('SourceTypeData') || [];
        const getListData = localStorageGetItem('PreferredContactMethodData') || [];
        const getReminderMethodData = localStorageGetItem('ReminderMethodData') || [];
        const getReminderPriorityData = localStorageGetItem('ReminderPriorityData') || [];
        const getReasonsData = localStorageGetItem('ReasonsData') || [];
        const getContactMethodData = localStorageGetItem('ContactMethodData') || [];
        const getAppointmentTypeData = localStorageGetItem('AppointmentTypeData') || [];
        const getAppointmentMethodData = localStorageGetItem('AppointmentMethodData') || [];
        const getSalutationOptionsData = localStorageGetItem('SalutationOptions') || [];
        let getArchiveReasonsData = localStorageGetItem('ArchiveReasonsData') || [];
        // Invalidate stale cache: the old filter kept only 2 values (NOT_INTERESTED,
        // DUPLICATE_ENTRY). The full Note enum has 10+ values. Force a refetch if
        // the stored list is suspiciously small so AppointmentPage gets all buttons.
        if (getArchiveReasonsData.length > 0 && getArchiveReasonsData.length < 5) {
            localStorageRemoveItem('ArchiveReasonsData');
            getArchiveReasonsData = [];
        }
        const getFinanceTermOptionsData = localStorageGetItem('FinanceTermOptions') || [];
        const getreportTypeData = localStorageGetItem('ReportTypeData') || [];
        const getRefundPaymentReasonData = localStorageGetItem('RefundPaymentReasonData') || [];
        if (getListData?.length === 0 || getReferralData?.length === 0 || getSourceTypeData?.length === 0 || getReminderMethodData?.length === 0 || getReminderPriorityData?.length === 0 || getReasonsData?.length === 0 || getContactMethodData?.length === 0 || getAppointmentTypeData?.length === 0 || getAppointmentMethodData?.length === 0 || getSalutationOptionsData?.length === 0 || getArchiveReasonsData?.length === 0 || getFinanceTermOptionsData?.length === 0 || getreportTypeData?.length === 0 || getRefundPaymentReasonData?.length === 0) {
            dispatch(getSchemaList());
        }
        if (isEmpty(globalTablePreferences)) {
            dispatch(getGlobalTablePreferences());
        }
        if (isEmpty(choicesData) && practiceId) {
            dispatch(getChoicesList(practiceId));
        }
        if (getMergeTagsData?.length === 0) {
            dispatch(getMergeTags());
        }
    }, [practiceId, isPractitioner, dentallyFeature?.canView, permLoading, permLoadingPracticeId]);

    // ── Dentally probe — one request per practice, every role ────────────────
    // Split out of the lookup-seeding effect above, which skips Practitioner
    // users entirely. That skip left the app-global `dentallyEnabledGlobal`
    // flag (seeded from localStorage) permanently `true` for a practitioner who
    // had once visited a Dentally practice, and left the per-practice config
    // unloaded so every Dentally gate stayed unsettled. Both are practice
    // identity, not role-specific data, so this runs for everyone.
    //
    // getDentallyConfigs is now the single probe: it loads the practice-scoped
    // config that useDentallyIntegrationScope gates on AND derives the global
    // flag from the same response, so one GET does what checkDentallyEnabled
    // plus a separate config fetch used to do.
    useEffect(() => {
        if (!practiceId) return;
        if (dentallyFeature?.canView) {
            // Gated on canView so the API isn't hit (and 403s) for practices
            // where the dentally-integration flag is hidden — Sentry noise.
            dispatch(getDentallyConfigs(practiceId));
        } else if (!permLoading && permLoadingPracticeId != null
            && Number(permLoadingPracticeId) === Number(practiceId)) {
            // Permissions have RESOLVED for this practice and dentally-integration
            // is hidden → Dentally is genuinely off here. Clear any stale `true`
            // that leaked from a previously-viewed Dentally practice. Direct
            // dispatch — no API call, so no 403 flood and no bootstrap flash for
            // real Dentally practices (their canView flips true and takes the
            // branch above). Also rewrites the localStorage cache to 'false'.
            dispatch(updateDentallyEnabledGlobal(false));
        }
    }, [practiceId, dentallyFeature?.canView, permLoading, permLoadingPracticeId]);

    // Practitioner prefetch waits for an ACTIVE config for THIS practice, not
    // just the permission. Firing on permission alone called
    // /practices/{id}/dentally/practitioners/ for practices with no integration
    // at all (2026-09-19 gating audit, case B).
    const dentallyScope = useDentallyIntegrationScope(practiceId);
    useEffect(() => {
        if (!practiceId || !dentallyScope.visible) return;
        dispatch(prefetchDentallyPractitioners(practiceId));
    }, [practiceId, dentallyScope.visible]);

    const handleCallBack = async () => {
        await logout();
    };

    const changeValue = (id) => {
        setSelectedId(id);
        dispatch(switchPractice(id));
    };

    const onChangeSelect = (event) => {
        const triggeredFromInterPracticePage = event?.target?.type === "INTER-PRACTICE-CHANGE" ?? false;
        const nextPracticeId = event.target.value;
        // Wipe every practice-scoped slice FIRST, so the explicit table-default
        // dispatches further down still get the last word. Without this, any
        // slice not named in that hand-maintained list below keeps serving the
        // OUTGOING practice's rows to whatever page we stay on until that
        // page's own fetch resolves. See redux/rootReducer.js.
        dispatch(practiceSwitchReset());
        if (!triggeredFromInterPracticePage) {
            // Stay on the page the operator is already looking at instead of
            // hard-resetting to the dashboard. resolvePracticeSwitchTarget()
            // decides between keeping the path, rewriting its /practice/<id>/
            // segment, and dropping an old-practice entity id for that
            // section's list page (or the dashboard when there isn't one).
            // The query string is intentionally discarded: params such as
            // ?lead= / ?templateId= / ?highlight= reference records that only
            // exist under the OUTGOING practice.
            const target = resolvePracticeSwitchTarget({ pathname, toPracticeId: nextPracticeId });
            const staysPut = target === pathname;
            // Super-admin URLs keep their own filter state in the query string
            // and are not practice-owned — leave them byte-for-byte alone.
            const dropsQuery = Boolean(search) && !shouldPreserveQuery(pathname);
            // Force the routed page to remount even when the URL is unchanged
            // — see practiceTreatmentList.practiceSwitchEpoch.
            dispatch(bumpPracticeSwitchEpoch());
            if (!staysPut || dropsQuery) {
                // `replace` on a stay-put switch so repeated switching does not
                // pile identical entries into history; a genuine redirect keeps
                // push semantics, matching the previous navigate('/dashboard').
                navigate(target, { replace: staysPut });
            }
            // Feature availability and the Tasks V1/V2 split are per-practice
            // and only known once fetchMyPermissions(nextPracticeId) lands.
            dispatch(setPendingSwitchLanding({ practiceId: `${nextPracticeId}`, pathname: target }));
        }
        changeValue(nextPracticeId);
        dispatch(reset());
        dispatch(resetTablePreferences());
        dispatch(clearTablePreferences());
        dispatch(clearTreatmentsListForWidget());
        dispatch(practiceIntegrationReset());
        dispatch(setToDoData([]));
        dispatch(setEmailNotifications([]));
        dispatch(setWpNotifications([]));
        dispatch(setReminderData([]));
        dispatch(chatReset());
        dispatch(callScriptReset());
        dispatch(audienceManagerReset());
        dispatch(resetDentallyPractitioners());
        dispatch(addTreatmentsList([]));
        dispatch(clearTableData());
        dispatch(getPracticeData({}));
        dispatch(updateCounter([]));
        dispatch(clearTaskLead());
        dispatch(clearData());
        dispatch(clearSeparateTaskCounter());
        dispatch(clearTreatmentCategoryReport());
        dispatch(clearMediaImages());
        dispatch(setWpTemplateListGlobal(null));
        dispatch(notificationData([{
            results: [],
            count: 0
        }]));
        dispatch(updateArchiveTable({
            pageNumber: 0,
            perPage: 10,
        }));
        dispatch(updateDashNewLeadsTable({
            count: '0',
            labelCounter: 0,
            pageNumber: 0,
            perPage: 10,
            order: 'desc',
            orderValue: 'created_at',
            search: '',
            treatment: [],
            date: [],
            status: 'NEW',
            tag: [],
            filterTags: [],
            referral: ''
        }));
        dispatch(updateBookedTable({
            count: 'All',
            pageNumber: 0,
            perPage: 10,
            order: 'desc',
            orderValue: 'created_at',
            search: '',
            treatment: [],
            date: [],
            status: 'CONSULTATION_BOOKED',
            tag: [],
            filterTags: [],
            referral: '',
            bookedWith: 0
        }));
        dispatch(updateFailedToAttendTable({
            pageNumber: 0,
            perPage: 10,
            order: 'desc',
            orderValue: 'created_at',
            search: '',
            treatment: [],
            date: [],
            status: 'FAILED_TO_ATTEND_CONSULTATION',
            tag: [],
            filterTags: [],
            referral: '',
        }));
        dispatch(updateClosedTable({
            pageNumber: 0,
            perPage: 10,
            order: 'desc',
            orderValue: 'created_at',
            search: '',
            treatment: [],
            date: [],
            status: 'CLOSED',
            tag: [],
            filterTags: [],
            referral: ''
        }));
        dispatch(updateAttendTable({
            count: '0',
            pageNumber: 0,
            perPage: 10,
            order: 'desc',
            orderValue: 'created_at',
            search: '',
            treatment: [],
            date: [],
            status: 'CONSULTATION_ATTENDED',
            tag: [],
            filterTags: [],
            referral: '',
            bookedWith: 0
        }));
        dispatch(updateNewTable({
            count: '0',
            pageNumber: 0,
            perPage: 10,
            order: 'desc',
            orderValue: 'created_at',
            search: '',
            treatment: [],
            date: [],
            status: 'NEW',
            tag: [],
            filterTags: [],
            referral: ''
        }));
        dispatch(updateTreatmentStartedTable({
            pageNumber: 0,
            perPage: 10,
            order: 'desc',
            orderValue: 'created_at',
            search: '',
            treatment: [],
            date: [],
            status: 'TREATMENT_STARTED',
            tag: [],
            filterTags: [],
            referral: '',
            category: '0'
        }));
        if (!triggeredFromInterPracticePage)
            dispatch(updateDashboardTable({
                count: 'All',
                pageNumber: 0,
                perPage: 10,
                order: 'desc',
                orderValue: 'created_at',
                search: '',
                treatment: [],
                date: [],
                status: 'TREATMENT_STARTED',
                tag: [],
                filterTags: [],
                referral: ''
            }));
        dispatch(updateNoTaskTable({
            count: 'All',
            pageNumber: 0,
            perPage: 10,
            order: 'desc',
            orderValue: 'created_at',
            search: '',
            treatment: [],
            date: [],
            status: 'All',
            tag: [],
            filterTags: [],
            referral: ''
        }));
        localStorageRemoveItem('dateFormat');
        localStorageRemoveItem('ReferralData');
        localStorageRemoveItem('SourceTypeData');
        localStorageRemoveItem('PreferredContactMethodData');
        localStorageRemoveItem('ReminderMethodData');
        localStorageRemoveItem('ReminderPriorityData');
        localStorageRemoveItem('AppointmentTypeData');
        localStorageRemoveItem('AppointmentMethodData');
        localStorageRemoveItem('FinanceTermOptions');
        localStorageRemoveItem('ReportTypeData');
        localStorageRemoveItem('SalutationOptions');
        localStorageRemoveItem('ArchiveReasonsData');
        localStorageRemoveItem('RefundPaymentReasonData');
        localStorageRemoveItem('ContactMethodData');
        localStorageRemoveItem('ReasonsData');
        localStorageRemoveItem('MergeTagsData');
        // Practice-scoped caches that are NOT suffixed with a practice id, so
        // they would otherwise rehydrate the previous practice's values:
        // the analytics treatment filter list and the dashboard treatment draft.
        localStorageRemoveItem('report-treatment');
        localStorageRemoveItem('localTreatmentData');
        localStorageRemoveItem('reminderData');
        localStorageRemoveItem('timeout_ids');
        // NOTE: do NOT eagerly clearPermissions() here. Doing so causes
        // `hasTasksV2` to flip to false during the fetchMyPermissions network
        // window, briefly pointing the Tasks nav entry at the wrong URL and
        // flashing the wrong V2 chip state. `setPermissions` overwrites
        // atomically when the response arrives — the previous practice's
        // permissions are safe to keep visible for the ~300ms in between.
        // clearPermissions() is still called on logout in JWTContext.jsx.
        dispatch(fetchMyPermissions(nextPracticeId));
        dispatch(leadReset());
        dispatch(resetWhatsAppNotifCount());
    };

    // Cross-practice jump: the All Patients / Inter-Practice tables dispatch
    // updatePracticeIdToChange(row.practiceId) to switch the operator into the
    // practice that owns the row they clicked. shouldApplyPendingPracticeChange
    // skips a "switch" to the practice already selected, which is what a cold
    // page load looks like — see that helper for why, and for the duplicate
    // my-permissions request it was causing.
    useEffect(() => {
        if (!shouldApplyPendingPracticeChange(practiceIdToChange, practiceId)) return;
        onChangeSelect({ target: { value: practiceIdToChange, type: "INTER-PRACTICE-CHANGE" } });
    }, [practiceIdToChange]);

    // Post-switch reconciliation.
    //
    // Some routes are only valid for practices that have a given feature
    // (Dentally, CA Pipeline v2, Aligner Approve, Geo Locator, Chatbot) or a
    // given Tasks version. Those answers arrive with the new practice's
    // permissions, i.e. AFTER the navigation above. When they land, bounce off
    // a page the new practice cannot show — to the dashboard, or, for Tasks, to
    // the other version's root so the operator stays on the same sidebar entry
    // instead of being ejected to /permission-denied.
    //
    // Fails open by design: if the permissions fetch errors, or the operator
    // navigates away first, nothing happens and they keep the page they chose.
    useEffect(() => {
        if (!pendingSwitchLanding) return;
        if (pathname !== pendingSwitchLanding.pathname) {
            dispatch(clearPendingSwitchLanding());
            return;
        }
        // Wait for a *successful* snapshot stamped with the target practice.
        // `permissions.practiceId` is only written on a 200; an empty map is
        // the pre-bootstrap state, not a genuine "no features" answer (same
        // gate FeatureGuard uses).
        if (permLoading) return;
        if (permissionsPracticeId == null) return;
        if (`${permissionsPracticeId}` !== pendingSwitchLanding.practiceId) return;
        if (!permissionsMap || Object.keys(permissionsMap).length === 0) return;

        dispatch(clearPendingSwitchLanding());
        const tasksV2Level = permissionsMap['tasks-v2'];
        const fallback = resolveRequirementFallback(getRouteRequirement(pathname), {
            hasFeature: (feature) => {
                const level = permissionsMap[feature];
                return level === 'view' || level === 'manage';
            },
            hasTasksV2: typeof tasksV2Level === 'string' && tasksV2Level !== 'hidden',
        });
        if (fallback && fallback !== pathname) {
            navigate(fallback, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingSwitchLanding, pathname, permLoading, permissionsPracticeId, permissionsMap]);

    const hasMultiplePractices = (practiceTreatment?.results?.length || 0) > 1;

    const renderContent = (
        <>

            {!isDesktop && !isPractitionerFlagUser && (
                <IconButton onClick={onOpenSidebar} sx={{ mr: 1, color: 'text.primary' }}>
                    <SvgColor src="/assets/icons/navbar/ic_menu_item.svg" />
                </IconButton>
            )}

            {!isPractitionerFlagUser && hasMultiplePractices && (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mr: { sm: 2, xs: 0.5 } }}>
                    <Tooltip title={demoMode ? 'Demo mode on — practice name hidden. Click to disable.' : 'Hide practice name (demo mode)'}>
                        <IconButton
                            size="small"
                            onClick={toggleDemoMode}
                            sx={{ mt: 0.5, color: demoMode ? 'warning.main' : 'text.secondary' }}
                            data-testid="practice-demo-mode-toggle"
                        >
                            <Iconify icon={demoMode ? 'eva:eye-off-fill' : 'eva:eye-outline'} width={18} height={18} />
                        </IconButton>
                    </Tooltip>
                    {demoMode ? (
                        <>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={(e) => setDemoMenuAnchor(e.currentTarget)}
                                endIcon={<Iconify icon="eva:chevron-down-fill" width={16} height={16} />}
                                data-testid="practice-demo-button"
                                sx={{
                                    mt: 0.5,
                                    width: { xs: 140, sm: 200, md: 250 },
                                    flexShrink: 0,
                                    justifyContent: 'space-between',
                                    fontSize: { sm: '14px', xs: '13px' },
                                    fontWeight: 400,
                                    color: 'text.primary',
                                    borderColor: 'rgba(145, 158, 171, 0.32)',
                                    textTransform: 'none',
                                }}
                            >
                                Practice
                            </Button>
                            <Menu
                                anchorEl={demoMenuAnchor}
                                open={Boolean(demoMenuAnchor)}
                                onClose={() => setDemoMenuAnchor(null)}
                                slotProps={{ paper: { sx: { minWidth: 250 } } }}
                            >
                                {(practiceTreatment?.results || []).map((p, i) => (
                                    <MenuItem
                                        key={p.id}
                                        selected={`${p.id}` === `${selectedId}`}
                                        onClick={() => {
                                            if (`${p.id}` !== `${selectedId}`) {
                                                onChangeSelect({ target: { value: `${p.id}` } });
                                            }
                                            setDemoMenuAnchor(null);
                                        }}
                                    >
                                        {`Practice ${i + 1}`}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    ) : (
                        <Autocomplete
                            id="select-practice-search"
                            options={practiceTreatment?.results || []}
                            getOptionLabel={(option) => option.name || ''}
                            value={practiceTreatment?.results?.find((p) => `${p.id}` === `${selectedId}`) || null}
                            onChange={(_, newValue) => {
                                if (newValue) {
                                    onChangeSelect({ target: { value: `${newValue.id}` } });
                                }
                            }}
                            isOptionEqualToValue={(option, value) => `${option.id}` === `${value.id}`}
                            disableClearable
                            size="small"
                            sx={{
                                mt: 0.5,
                                width: { xs: 140, sm: 200, md: 250 },
                                flexShrink: 0,
                                '& .MuiOutlinedInput-root': {
                                    fontSize: { sm: '14px', xs: '13px' },
                                },
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Practice"
                                    InputLabelProps={{
                                        ...params.InputLabelProps,
                                        sx: { fontSize: { sm: '14px', xs: '13px' } },
                                    }}
                                />
                            )}
                        />
                    )}
                </Stack>
            )}

            {!isPractitionerFlagUser && <Searchbar onOpenCommandPalette={onOpenCommandPalette} />}

            {/* V4 mock-status controls — only on /lead-detail-v3/ and /lead-detail-v4/ routes */}
            {(() => {
                const isV3Route = pathname.includes('/lead-detail-v3/');
                const isV4Route = pathname.includes('/lead-detail-v4/');
                const showMockControls = isV3Route || isV4Route;
                if (!showMockControls) return null;
                const versionLabel = isV4Route ? 'V4' : 'V3';
                const switchToLabel = isV4Route ? 'Switch to V3' : 'Switch to V2';
                const switchToPath = isV4Route
                    ? `/dashboard/practice/${routeParams.practiceId || '36'}/lead-detail-v3/${routeParams.id || '1001'}/overview`
                    : `/dashboard/practice/${routeParams.practiceId || '36'}/lead-detail-v2/${routeParams.id || '1001'}/overview`;
                return (
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mx: 1, flexShrink: 0 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(switchToPath)}
                            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', height: 30, whiteSpace: 'nowrap' }}
                        >
                            {switchToLabel}
                        </Button>
                        <Iconify icon="mdi:flask-outline" width={16} sx={{ color: 'secondary.main', flexShrink: 0 }} />
                        {isDesktop && (
                            <Typography variant="caption" sx={{ color: 'secondary.main', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {versionLabel}
                            </Typography>
                        )}
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <Select
                                value={searchParams.get('mockStatus') || 'MULTI_TREATMENT'}
                                onChange={(e) =>
                                    setSearchParams((prev) => {
                                        prev.set('mockStatus', e.target.value);
                                        return prev;
                                    })
                                }
                                size="small"
                                sx={{ fontSize: '0.78rem', height: 30 }}
                            >
                                {V3_MOCK_KEYS.map((key) => (
                                    <MenuItem key={key} value={key}>{V3_MOCK_LABELS[key]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                );
            })()}

            {/* The sign-in desk prompt renders here, in the row, never over the page (DeskPromptPill). */}
            {!isPractitionerFlagUser && (
                <Box
                    id={HEADER_DESK_SLOT_ID}
                    data-testid="header-desk-slot"
                    sx={{ ml: 'auto', mr: { xs: 0.5, sm: 1 }, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
                />
            )}
            <Stack
                flexShrink={0}
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                spacing={{ xs: 0.5, sm: 1 }}
                sx={{ ml: isPractitionerFlagUser ? 'auto' : 0 }}
            >
                {isPractitionerFlagUser ? <AccountPopover /> : (
                    <>
                        <MyDeskChip compact sx={{ display: { xs: 'none', sm: 'inline-flex' }, maxWidth: 180 }} />
                        <InstantVideoSwitch />
                        {/* Practice-wide pinned leads; renders nothing while there are none. */}
                        <PinnedLeadsPopover />
                        <NotificationsPopover />
                        {/* Switch-User moved into the profile menu (2026-07-04) — the
                            standalone users-group icon is gone; AccountPopover renders
                            the same ContactsPopover in controlled mode. Practitioner
                            users keep no Switch-User access (same gate as before). */}
                        <SuperAdminPopover />
                        <SettingsPopover />
                        <AccountPopover showSwitchUser />
                    </>
                )}
            </Stack>
        </>
    );

    return (
        <AppBar
            sx={{
                boxShadow: 'none',
                height: HEADER.H_MOBILE,
                zIndex: theme.zIndex.appBar + 1,
                ...bgBlur({
                    color: theme.palette.background.default,
                }),
                transition: theme.transitions.create(['height'], {
                    duration: theme.transitions.duration.shorter,
                }),
                ...(isDesktop && {
                    width: `calc(100% - ${NAV.W_DASHBOARD + 1}px)`,
                    height: HEADER.H_DASHBOARD_DESKTOP,
                    ...(isOffset && {
                        height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
                    }),
                    ...(isNavHorizontal && {
                        width: 1,
                        bgcolor: 'background.default',
                        height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
                        borderBottom: `dashed 1px ${theme.palette.divider}`,
                    }),
                    ...(isNavMini && {
                        width: `calc(100% - ${NAV.W_DASHBOARD_MINI + 1}px)`,
                    }),
                }),
            }}
        >
            <Toolbar
                sx={{
                    height: 1,
                    px: { lg: 3 },
                }}
            >
                {renderContent}
            </Toolbar>
        </AppBar>
    );
}

DashboardHeader.propTypes = {
    onOpenSidebar: PropTypes.func,
    onOpenCommandPalette: PropTypes.func,
};